import { colorFor } from "./palette";

/**
 * Retention as KPI tiles, one per product.
 *
 * The rate is the headline, but a rate alone hides its own denominator — 94%
 * over sixteen services means something different from 94% over four hundred.
 * So each tile carries the retained-of-cohort count underneath, and for value
 * measures the amounts too.
 *
 * For net revenue retention the bar to beat is 100%, not "all of it": growth in
 * survivors can outweigh what churned, so above 100 reads as good and is
 * coloured accordingly rather than looking like an overflow.
 */
export default function CohortTiles({ groups = [], measure = "units", onGroupClick }) {
  if (!groups.length) {
    return <p className="py-12 text-center text-sm text-[#4a5b68]">No records in this cohort.</p>;
  }

  const isMoney = measure !== "units";
  const money = (value) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(value || 0);
  const count = (value) => new Intl.NumberFormat("en-GB").format(value || 0);

  // NRR is judged against 100%; the other two against how much was kept.
  const toneFor = (rate) => {
    if (rate === null || rate === undefined) return "text-[#7d8b96]";
    if (measure === "nrr") return rate >= 100 ? "text-emerald-600" : rate >= 90 ? "text-amber-600" : "text-red-600";
    return rate >= 90 ? "text-emerald-600" : rate >= 75 ? "text-amber-600" : "text-red-600";
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="cohort-tiles">
      {groups.map((group, index) => (
        <div
          key={group.key || group.label}
          role={onGroupClick ? "button" : undefined}
          tabIndex={onGroupClick ? 0 : undefined}
          onClick={() => onGroupClick && onGroupClick(group.label)}
          onKeyDown={(event) => {
            if (onGroupClick && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              onGroupClick(group.label);
            }
          }}
          className={`rounded-md border border-[#cccccc] bg-white p-4 ${
            onGroupClick ? "cursor-pointer transition-colors hover:border-[#57c1a9]" : ""
          }`}
          data-testid={`cohort-tile-${group.label}`}
        >
          <div className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
              style={{ backgroundColor: colorFor(group.label, index, {}) }}
            />
            <p
              className="truncate text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]"
              title={group.label}
            >
              {group.label}
            </p>
          </div>

          <p className={`mt-2 font-sans text-2xl font-bold leading-none ${toneFor(group.rate)}`}>
            {group.rate === null || group.rate === undefined ? "—" : `${group.rate}%`}
          </p>

          <p className="mt-1.5 text-xs text-[#4a5b68]">
            {count(group.retained_count)} of {count(group.cohort_count)} retained
          </p>
          {isMoney && (
            <p className="mt-0.5 text-[11px] text-[#7d8b96]">
              {money(group.retained)} of {money(group.cohort)}
            </p>
          )}
          {/* The forecast sits on the tile as well as in the table. Without
              it a tile reads 100% beside a revenue report forecasting a loss,
              and the two look like they disagree when they do not. */}
          {group.forecast_loss > 0 && (
            <p className="mt-0.5 text-[11px] text-amber-700">
              Forecast at year end:{" "}
              {isMoney
                ? money(group.forecast_remaining_value)
                : `${count(group.forecast_remaining)}`}
              {" "}({count(group.forecast_loss)} expected to go)
            </p>
          )}
          {/* From Renewal status, kept apart from the forecast. */}
          {group.unlikely_count > 0 && (
            <p className="mt-0.5 text-[11px] text-amber-700">
              Unlikely to renew: {count(group.unlikely_count)}
              {isMoney ? ` (${money(group.unlikely_value)})` : ""}
            </p>
          )}
          {!isMoney && group.target !== null && group.target !== undefined && (
            <p className="mt-0.5 text-[11px] text-[#7d8b96]">Target {count(group.target)}</p>
          )}

          {/* A small bar makes the tiles comparable at a glance without
              needing a second chart. NRR can exceed 100, so it is capped. */}
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#efefef]">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(Math.max(group.rate || 0, 0), 100)}%`,
                backgroundColor: colorFor(group.label, index, {}),
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
